import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Descriptions,
    Drawer,
    Form,
    Input,
    InputNumber,
    Modal,
    Radio,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    message,
} from 'antd';
import { AppstoreOutlined, BarsOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import PageHeader from '../../components/business/PageHeader';
import PermissionGuard from '../../components/business/PermissionGuard';
import RequestState from '../../components/business/RequestState';
import StatusTag, { statusLabel } from '../../components/business/StatusTag';
import { Opportunity, PageResult } from '../../models/v2';
import { V2ApiError, v2Api } from '../../services/v2';
import { asV2Error, formatDate, formatDateTime, formatMoney } from '../../utils/v2';

const stages = [
    'DEMAND_CONFIRMED',
    'SOLUTION_DISCUSSION',
    'SOLUTION_APPROVED',
    'SOLUTION_REJECTED',
    'DEMAND_CANCELLED',
    'COMMERCIAL_COMPLETED',
];
const activeStages = ['DEMAND_CONFIRMED', 'SOLUTION_DISCUSSION', 'SOLUTION_APPROVED'];
const stageTransitions: { [key: string]: string[] } = {
    DEMAND_CONFIRMED: ['SOLUTION_DISCUSSION', 'DEMAND_CANCELLED'],
    SOLUTION_DISCUSSION: ['SOLUTION_APPROVED', 'SOLUTION_REJECTED', 'DEMAND_CANCELLED'],
    SOLUTION_APPROVED: ['COMMERCIAL_COMPLETED', 'DEMAND_CANCELLED'],
};
const emptyPage: PageResult<Opportunity> = { items: [], pageNo: 1, pageSize: 20, total: 0 };

const OpportunityPage = () => {
    const [createForm] = Form.useForm();
    const [transitionForm] = Form.useForm();
    const [data, setData] = useState(emptyPage);
    const [filters, setFilters] = useState<any>({ pageNo: 1, pageSize: 20, sort: '-createdAt' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<V2ApiError | null>(null);
    const [view, setView] = useState<'list' | 'board'>('list');
    const [detail, setDetail] = useState<Opportunity | null>(null);
    const [quotes, setQuotes] = useState<any[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [createVisible, setCreateVisible] = useState(false);
    const [transitionVisible, setTransitionVisible] = useState(false);
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setData(await v2Api.opportunities(filters));
        } catch (e) {
            setError(asV2Error(e));
        } finally {
            setLoading(false);
        }
    }, [filters]);
    useEffect(() => {
        load();
    }, [load]);
    useEffect(() => {
        const open = new URLSearchParams(window.location.hash.split('?')[1] || '').get('open');
        if (open) openDetail(Number(open));
    }, []);
    const openDetail = async (id: number) => {
        setDetailLoading(true);
        setDetail({ id } as Opportunity);
        try {
            const result = await v2Api.opportunity(id);
            setDetail(result);
            try {
                setQuotes(await v2Api.quotes(id));
            } catch (_) {
                setQuotes([]);
            }
        } catch (e) {
            message.error(asV2Error(e).message);
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    };
    const create = async () => {
        const values = await createForm.validateFields();
        const body: any = {
            ...values,
            companyId: Number(values.companyId),
            ownerId: Number(values.ownerId),
            nextActionAt: new Date(values.nextActionAt).toISOString(),
            contacts: [
                {
                    employmentId: Number(values.employmentId),
                    participantPartyType: 'CUSTOMER',
                    opportunityRole: 'PRIMARY_CONTACT',
                    primary: true,
                },
            ],
        };
        delete body.employmentId;
        setSaving(true);
        try {
            const result = await v2Api.createOpportunity(body);
            message.success('商机已创建');
            setCreateVisible(false);
            createForm.resetFields();
            load();
            openDetail(result.id);
        } catch (e) {
            message.error(asV2Error(e).message);
        } finally {
            setSaving(false);
        }
    };
    const transition = async () => {
        if (!detail) return;
        const values = await transitionForm.validateFields();
        if (
            values.toStage === 'COMMERCIAL_COMPLETED' &&
            !values.finalAmount &&
            !values.amountPendingReason
        ) {
            transitionForm.setFields([
                { name: 'finalAmount', errors: ['最终金额或金额待定说明至少填写一项'] },
            ]);
            return;
        }
        const body = {
            ...values,
            version: detail.version,
            nextActionAt: values.nextActionAt
                ? new Date(values.nextActionAt).toISOString()
                : undefined,
        };
        setSaving(true);
        try {
            const result = await v2Api.transitionOpportunity(detail.id, body);
            message.success('商机阶段已更新');
            setDetail(result);
            setTransitionVisible(false);
            load();
        } catch (e) {
            const err = asV2Error(e);
            message.error(
                err.code === 'VERSION_CONFLICT'
                    ? '商机已被其他人更新，请重新打开后再提交'
                    : err.message
            );
        } finally {
            setSaving(false);
        }
    };
    const grouped = useMemo(
        () =>
            stages.reduce((map: any, stage) => {
                map[stage] = data.items.filter((item) => item.stageCode === stage);
                return map;
            }, {}),
        [data.items]
    );
    const columns = [
        {
            title: '商机 / 企业',
            key: 'name',
            width: 230,
            render: (_: any, row: Opportunity) => (
                <Button type="link" className="v2-link-strong" onClick={() => openDetail(row.id)}>
                    {row.name}
                    <span className="v2-subline">{row.companyName}</span>
                </Button>
            ),
        },
        {
            title: '阶段',
            dataIndex: 'stageCode',
            width: 130,
            render: (v: string) => <StatusTag value={v} />,
        },
        {
            title: '业务领域',
            dataIndex: 'businessDomainCode',
            width: 120,
            render: (v: string) => v || '待完善',
        },
        {
            title: '预计金额',
            key: 'amount',
            width: 150,
            align: 'right' as const,
            render: (_: any, row: Opportunity) => formatMoney(row.estimatedAmount, row.currency),
        },
        { title: '预计决策日', dataIndex: 'expectedDecisionDate', width: 120, render: formatDate },
        {
            title: '下一步行动',
            key: 'next',
            width: 230,
            render: (_: any, row: Opportunity) => (
                <div>
                    {row.nextAction || <span className="v2-danger">缺少下一步</span>}
                    <div className="v2-muted">{formatDateTime(row.nextActionAt)}</div>
                </div>
            ),
        },
        {
            title: '负责人',
            dataIndex: 'ownerName',
            width: 100,
            render: (v: string, row: Opportunity) => v || row.ownerId || '—',
        },
    ];
    return (
        <div className="v2-page">
            <PageHeader
                eyebrow="商机 · 客户需求推进"
                title="商机"
                description="列表与看板共享同一数据。阶段只能通过明确业务动作推进，日期逾期是提醒而不是阶段。"
                extra={
                    <Space>
                        <Radio.Group value={view} onChange={(e) => setView(e.target.value)}>
                            <Radio.Button value="list">
                                <BarsOutlined /> 列表
                            </Radio.Button>
                            <Radio.Button value="board">
                                <AppstoreOutlined /> 看板
                            </Radio.Button>
                        </Radio.Group>
                        <Button icon={<ReloadOutlined />} onClick={load}>
                            刷新
                        </Button>
                        <PermissionGuard anyOf={['OPPORTUNITY_EDIT']}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateVisible(true)}
                            >
                                创建商机
                            </Button>
                        </PermissionGuard>
                    </Space>
                }
            />
            <Card className="v2-filter-card">
                <Space className="v2-wrap-space">
                    <Select
                        allowClear
                        placeholder="商机阶段"
                        style={{ width: 180 }}
                        onChange={(stage) => setFilters({ ...filters, stage, pageNo: 1 })}
                    >
                        {stages.map((stage) => (
                            <Select.Option key={stage} value={stage}>
                                {statusLabel(stage)}
                            </Select.Option>
                        ))}
                    </Select>
                    <InputNumber
                        min={1}
                        placeholder="企业 ID"
                        onChange={(companyId) => setFilters({ ...filters, companyId, pageNo: 1 })}
                    />
                    <InputNumber
                        min={1}
                        placeholder="负责人 ID"
                        onChange={(ownerId) => setFilters({ ...filters, ownerId, pageNo: 1 })}
                    />
                </Space>
            </Card>
            <RequestState loading={loading} error={error} empty={!data.items.length} onRetry={load}>
                {view === 'list' ? (
                    <Card className="v2-panel" bordered={false}>
                        <Table
                            rowKey="id"
                            columns={columns}
                            dataSource={data.items}
                            scroll={{ x: 1120 }}
                            pagination={{
                                current: data.pageNo,
                                pageSize: data.pageSize,
                                total: data.total,
                                showSizeChanger: true,
                                onChange: (pageNo, pageSize) =>
                                    setFilters({ ...filters, pageNo, pageSize }),
                            }}
                        />
                    </Card>
                ) : (
                    <div className="v2-board">
                        {stages.map((stage) => (
                            <section className="v2-board__column" key={stage}>
                                <header>
                                    <StatusTag value={stage} />
                                    <strong>{grouped[stage].length}</strong>
                                </header>
                                {grouped[stage].map((item: Opportunity) => (
                                    <Card
                                        size="small"
                                        hoverable
                                        key={item.id}
                                        onClick={() => openDetail(item.id)}
                                    >
                                        <strong>{item.name}</strong>
                                        <p className="v2-muted">{item.companyName}</p>
                                        <p>{formatMoney(item.estimatedAmount, item.currency)}</p>
                                        <div className="v2-board__next">
                                            {item.nextAction || '缺少下一步行动'}
                                        </div>
                                    </Card>
                                ))}
                            </section>
                        ))}
                    </div>
                )}
            </RequestState>
            <Drawer
                width={820}
                title={
                    <div className="v2-drawer-title">
                        <span>{detail && detail.name}</span>
                        {detail && activeStages.includes(detail.stageCode) && (
                            <PermissionGuard anyOf={['OPPORTUNITY_EDIT']}>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        transitionForm.resetFields();
                                        setTransitionVisible(true);
                                    }}
                                >
                                    推进 / 关闭阶段
                                </Button>
                            </PermissionGuard>
                        )}
                    </div>
                }
                visible={!!detail}
                onClose={() => setDetail(null)}
                destroyOnClose
            >
                <RequestState
                    loading={detailLoading}
                    empty={false}
                    onRetry={() => detail && openDetail(detail.id)}
                >
                    {detail && (
                        <Tabs defaultActiveKey="overview">
                            <Tabs.TabPane tab="概览" key="overview">
                                <div className="v2-detail-hero">
                                    <StatusTag value={detail.stageCode} />
                                    <h3>{detail.nextAction || '缺少下一步行动'}</h3>
                                    <p>{formatDateTime(detail.nextActionAt)}</p>
                                </div>
                                <Descriptions bordered size="small" column={2}>
                                    <Descriptions.Item label="企业">
                                        {detail.companyName}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="负责人">
                                        {detail.ownerName || detail.ownerId}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="业务领域">
                                        {detail.businessDomainCode || '—'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="形成方式">
                                        {detail.formationMode}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="预计金额">
                                        {formatMoney(detail.estimatedAmount, detail.currency)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="预算金额">
                                        {formatMoney(detail.budgetAmount, detail.currency)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="问题场景" span={2}>
                                        {detail.problemScenario}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="期望结果" span={2}>
                                        {detail.expectedResult || '—'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Tabs.TabPane>
                            <Tabs.TabPane
                                tab={`客户联系人 ${
                                    Array.isArray(detail.contacts) ? detail.contacts.length : 0
                                }`}
                                key="contacts"
                            >
                                {Array.isArray(detail.contacts) &&
                                    detail.contacts.map((item: any) => (
                                        <div className="v2-related-row" key={item.id}>
                                            <strong>{item.personName}</strong>
                                            <span>
                                                {[item.department, item.positionTitle]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </span>
                                            {item.primary && <Tag color="blue">主要对接人</Tag>}
                                        </div>
                                    ))}
                            </Tabs.TabPane>
                            <Tabs.TabPane tab={`报价测算 ${quotes.length}`} key="quotes">
                                {quotes.length ? (
                                    quotes.map((quote) => (
                                        <Card
                                            key={quote.id}
                                            size="small"
                                            className="v2-section"
                                            title={`V${quote.versionNo} · ${quote.status}`}
                                            extra={formatMoney(
                                                quote.finalPrice || quote.suggestedPrice,
                                                quote.currency
                                            )}
                                        >
                                            <p>
                                                总成本：
                                                {formatMoney(quote.totalCost, quote.currency)} ·
                                                毛利：
                                                {formatMoney(quote.grossProfit, quote.currency)}
                                            </p>
                                        </Card>
                                    ))
                                ) : (
                                    <Alert
                                        type="info"
                                        showIcon
                                        message="尚无报价版本"
                                        description="报价新建接口已实现；报价定版需要近期 MFA，当前认证接口未完成，因此暂不开放定版按钮。"
                                    />
                                )}
                            </Tabs.TabPane>
                            <Tabs.TabPane tab="阶段与操作历史" key="history">
                                <Alert
                                    type="info"
                                    showIcon
                                    message="历史已由服务端落库"
                                    description="当前服务端尚未提供独立阶段历史查询接口，本页不拼接或伪造历史。"
                                />
                            </Tabs.TabPane>
                        </Tabs>
                    )}
                </RequestState>
            </Drawer>
            <Modal
                width={680}
                title="创建商机"
                visible={createVisible}
                onOk={create}
                confirmLoading={saving}
                onCancel={() => setCreateVisible(false)}
                okText="创建"
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    initialValues={{
                        formationMode: 'DIRECT',
                        currency: 'CNY',
                        solutionWillingnessConfirmed: true,
                        budgetWillingnessConfirmed: true,
                    }}
                >
                    <Form.Item label="商机名称" name="name" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Space style={{ display: 'flex' }}>
                        <Form.Item label="企业 ID" name="companyId" rules={[{ required: true }]}>
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item label="负责人 ID" name="ownerId" rules={[{ required: true }]}>
                            <InputNumber min={1} />
                        </Form.Item>
                        <Form.Item
                            label="主要对接人的任职 ID"
                            name="employmentId"
                            rules={[{ required: true }]}
                        >
                            <InputNumber min={1} />
                        </Form.Item>
                    </Space>
                    <Form.Item
                        label="业务问题 / 培训场景"
                        name="problemScenario"
                        rules={[{ required: true }]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Space>
                        <Form.Item name="solutionWillingnessConfirmed" valuePropName="checked">
                            <Checkbox>客户解决意愿已确认</Checkbox>
                        </Form.Item>
                        <Form.Item name="budgetWillingnessConfirmed" valuePropName="checked">
                            <Checkbox>预算投入意愿已确认</Checkbox>
                        </Form.Item>
                    </Space>
                    <Space style={{ display: 'flex' }}>
                        <Form.Item
                            label="形成方式"
                            name="formationMode"
                            rules={[{ required: true }]}
                        >
                            <Select style={{ width: 160 }}>
                                <Select.Option value="DIRECT">直接创建</Select.Option>
                                <Select.Option value="SIGNAL">客户信号</Select.Option>
                                <Select.Option value="REFERRAL">转介绍</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="币种" name="currency" rules={[{ required: true }]}>
                            <Select style={{ width: 100 }}>
                                <Select.Option value="CNY">CNY</Select.Option>
                                <Select.Option value="USD">USD</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item label="预计金额" name="estimatedAmount">
                            <Input />
                        </Form.Item>
                    </Space>
                    <Form.Item label="下一步行动" name="nextAction" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="行动时间" name="nextActionAt" rules={[{ required: true }]}>
                        <Input type="datetime-local" />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="商机阶段操作"
                visible={transitionVisible}
                onOk={transition}
                confirmLoading={saving}
                onCancel={() => setTransitionVisible(false)}
                okText="确认提交"
            >
                <Form form={transitionForm} layout="vertical">
                    <Form.Item label="目标阶段" name="toStage" rules={[{ required: true }]}>
                        <Select>
                            {(detail ? stageTransitions[detail.stageCode] || [] : []).map(
                                (stage) => (
                                    <Select.Option key={stage} value={stage}>
                                        {statusLabel(stage)}
                                    </Select.Option>
                                )
                            )}
                        </Select>
                    </Form.Item>
                    <Form.Item label="操作依据 / 原因" name="reason" rules={[{ required: true }]}>
                        <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, next) => prev.toStage !== next.toStage}>
                        {({ getFieldValue }) =>
                            getFieldValue('toStage') === 'COMMERCIAL_COMPLETED' ? (
                                <>
                                    <Form.Item
                                        label="商务完成依据"
                                        name="commercialCompletionBasis"
                                        rules={[{ required: true }]}
                                    >
                                        <Input.TextArea rows={2} />
                                    </Form.Item>
                                    <Space style={{ display: 'flex' }}>
                                        <Form.Item label="最终金额" name="finalAmount">
                                            <Input />
                                        </Form.Item>
                                        <Form.Item label="金额待定说明" name="amountPendingReason">
                                            <Input />
                                        </Form.Item>
                                    </Space>
                                </>
                            ) : activeStages.includes(getFieldValue('toStage')) ? (
                                <>
                                    <Form.Item
                                        label="下一步行动"
                                        name="nextAction"
                                        rules={[{ required: true }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Form.Item
                                        label="行动时间"
                                        name="nextActionAt"
                                        rules={[{ required: true }]}
                                    >
                                        <Input type="datetime-local" />
                                    </Form.Item>
                                </>
                            ) : null
                        }
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
export default OpportunityPage;
